import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    Button,
    TextField,
    useTheme,
    Fade,
    Link,
    InputAdornment,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAuth } from "../hooks/useAuth";
import { toast } from "react-toastify";
import "cropperjs/dist/cropper.css";
import { ArrowBack, Logout, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {t} from "i18next";

// App Info
const APP_VERSION = "v2.4.1";
const COPYRIGHT_YEAR = new Date().getFullYear();

const Settings = () => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const { session, checkSession, logout, api } = useAuth();
    const theme = useTheme();
    const navigate = useNavigate();



    const validatePassword = (pwd: string) => {
        const minLength = 8;
        const hasUpper = /[A-Z]/.test(pwd);
        const hasLower = /[a-z]/.test(pwd);
        const hasNumber = /\d/.test(pwd);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        if (pwd.length < minLength) return t("common:validation.passwordMinLength");
        if (!hasUpper) return t("common:validation.passwordUpperCase");
        if (!hasLower) return t("common:validation.passwordLowerCase");
        if (!hasNumber) return t("common:validation.passwordNumber");
        if (!hasSpecial) return t("common:validation.passwordSpecial");
        return "";
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error(t("common:toast.error.passMissMatch"));
            return;
        }

        const validationError = validatePassword(newPassword);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        setLoading(true);
        const toastId = toast.loading(t("common:settings.changingPassword"));

        try {
            const res = await api.post(`/auth/change_password`, {
                current_password: currentPassword,
                new_password: newPassword,
            });

            if (res.status === 204) {
                toast.update(toastId, {
                    render: t("common:toast.success.password.save"),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                });
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
            } else {
                toast.update(toastId, {
                    render: (res as any).response.data.message,
                    type: "error",
                    isLoading: false,
                    autoClose: 4000,
                });
            }
        } catch (err: any) {
            toast.update(toastId, {
                render: err.response?.data?.message || t("common:toast.error.unknown"),
                type: "error",
                isLoading: false,
                autoClose: 4000,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Validate-only (no slide) so password change does not race other in-flight calls.
        void checkSession(true, { slide: false });
    }, []);

    return (
        <Box
            sx={{
                height: "100vh",
                width: "100vw",
                background:
                    theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflowY: "scroll",
            }}
        >
            {/* Main Content */}
            <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pb: 10 }}>
                {/* Header */}
                <Fade in timeout={600}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 1 }}>
                        {/* Back Button */}
                        <Tooltip title={t("common:ui.tooltips.goBack")}>
                            <IconButton
                                onClick={() => navigate(-1)} // Go back in history
                                sx={{
                                    bgcolor: "action.hover",
                                    color: "text.primary",
                                    "&:hover": {
                                        bgcolor: "action.selected",
                                        transform: "translateX(-2px)",
                                    },
                                    transition: "all 0.2s ease",
                                    boxShadow: 2,
                                }}
                                size="large"
                            >
                                <ArrowBack sx={{ fontSize: 28 }} />
                            </IconButton>
                        </Tooltip>
                        <Box
                            sx={{
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                borderRadius: "50%",
                                p: 1.5,
                                mr: 2,
                                boxShadow: 3,
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                sx={{
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    letterSpacing: "-0.5px",
                                }}
                            >
                                {t("common:settings.userTitle")}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t("common:settings.userSubtitle")}
                            </Typography>
                        </Box>
                        {/* Logout Button – Pushed to the far right */}
                        <Tooltip title={t("common:ui.tooltips.signOut")}>
                            <IconButton
                                onClick={() => { void logout(); }}
                                sx={{
                                    bgcolor: "error.main",
                                    color: "error.contrastText",
                                    "&:hover": {
                                        bgcolor: "error.dark",
                                        transform: "translateY(-1px)",
                                    },
                                    transition: "all 0.2s ease",
                                    boxShadow: 3,
                                }}
                                size="large"
                            >
                                <Logout sx={{ fontSize: 26 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Fade>

                {/* Settings Grid */}
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }} >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                height: "100%",
                                border: `1px solid ${theme.palette.divider}`,
                                background: theme.palette.background.paper,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    boxShadow: 6,
                                    transform: "translateY(-2px)",
                                },
                            }}
                        >
                            {/* Password Form */}
                            <Box sx={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", p: 4 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        borderRadius: 3,
                                        width: "100%",
                                        maxWidth: 500,
                                        border: `1px solid ${theme.palette.divider}`,
                                        background: theme.palette.background.paper,
                                        boxShadow: 3,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        {t("common:settings.updatePassword")}
                                    </Typography>

                                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3 }}>
                                        <TextField
                                            label={t("common:ui.labels.currentPassword")}
                                            type={showCurrent ? "text" : "password"}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                            fullWidth
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">
                                                            {showCurrent ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <TextField
                                            label={t("common:ui.labels.newPassword")}
                                            type={showNew ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            fullWidth
                                            helperText={t("common:validation.passwordHelperText")}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                                                            {showNew ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <TextField
                                            label={t("common:ui.labels.confirmNewPassword")}
                                            type={showConfirm ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            fullWidth
                                            error={confirmPassword !== "" && newPassword !== confirmPassword}
                                            helperText={
                                                confirmPassword !== "" && newPassword !== confirmPassword
                                                    ? t("common:validation.passwordsDoNotMatch")
                                                    : " "
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                                                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                                            sx={{ mt: 2 }}
                                        >
                                            {loading ? t("common:settings.changingPassword") : t("common:ui.buttons.changePassword")}
                                        </Button>
                                    </Box>
                                </Paper>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    position: "relative",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: "background.paper",
                    color: "text.secondary",
                    py: 2.5,
                    px: 4,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    fontSize: "0.875rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>
                        <Link href="https://moleculesoft.net" target="_blank"
                            rel="noopener noreferrer">MOLeCULE Soft</Link>
                    </Typography>
                    <Typography variant="caption">© {COPYRIGHT_YEAR} {t("common:settings.allRightsReserved")}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    {t("common:settings.storage.systemVersion")} <strong style={{ color: theme.palette.text.primary }}>{APP_VERSION}</strong>
                </Typography>
            </Box>
        </Box>
    );
};

export default Settings;